import { useState } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { LoadingContainer } from '../../ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../ui/SortableHeader/SortableHeader';
import Container from '../../ui/Container/Container';
import useEmailLogLookup from './useEmailLogLookup';
import { Helpers } from '../../../utils/helpers';
import styles from './EmailLogLookup.module.css';

interface EmailLogLookupProps {
  emailLogId: string | undefined;
  onChange: (emailLogId: string) => void;
}

export const EmailLogLookup = ({ emailLogId, onChange }: EmailLogLookupProps) => {
  const manager = useEmailLogLookup(onChange);
  const [searchEmail, setSearchEmail] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchEmail(value);
    manager.debouncedEmailLogSearch(value);
  };

  const handleClearSearch = () => {
    setSearchEmail('');
    manager.resetSearch();
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'sent':
        return styles.statusSent;
      case 'delivered':
        return styles.statusDelivered;
      case 'pending':
        return styles.statusPending;
      case 'failed':
        return styles.statusFailed;
      default:
        return styles.statusDefault;
    }
  };

  return (
    <Container visible={true} className={styles.lookupSearchContainer}>
      <div className={styles.filterSection}>
        {/* Search Bar */}
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="email"
            placeholder="Enter an email address to search for sent emails"
            value={searchEmail}
            onChange={handleSearchChange}
          />
          <Button variant="outline-secondary" onClick={handleClearSearch} disabled={!searchEmail}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </InputGroup>

        {/* Search Results */}
        <div className={styles.lookupSearchResultContainer}>
          <LoadingContainer isLoading={manager.loadingSearch}>
            {/* No search performed yet */}
            <Container
              visible={!manager.hasSearchParameters && manager.emailLogResults.length === 0}>
              <div className={styles.initialState}>
                <FontAwesomeIcon icon={faSearch} className={styles.initialStateIcon} />
                <div className={styles.initialStateText}>Search for sent emails</div>
                <div className={styles.initialStateSubtext}>
                  Enter an email address above to search for sent emails
                </div>
              </div>
            </Container>

            {/* Search performed but no results */}
            <Container
              visible={
                manager.hasSearchParameters &&
                manager.emailLogResults.length === 0 &&
                !manager.loadingSearch
              }>
              <div className={styles.noResults}>
                <FontAwesomeIcon icon={faSearch} className={styles.noResultsIcon} />
                <div className={styles.noResultsText}>No emails found</div>
                <div className={styles.noResultsSubtext}>
                  No emails found for "{manager.searchParameters.recipientEmail}"
                </div>
              </div>
            </Container>

            {/* Results */}
            <Container visible={manager.emailLogResults.length > 0}>
              <div className={styles.lookupSearchResults}>
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      <SortableHeader
                        text="Sent Date"
                        sortField="sendDateUtc"
                        active={manager.searchParameters.sortField === 'sendDateUtc'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="To"
                        sortField="primaryRecipient"
                        active={manager.searchParameters.sortField === 'primaryRecipient'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Subject"
                        sortField="subject"
                        active={manager.searchParameters.sortField === 'subject'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manager.emailLogResults.map((emailLog) => (
                      <tr
                        key={emailLog.id}
                        onClick={() => manager.selectEmailLog(emailLog.id)}
                        className={styles.clickableRow}>
                        <td>{formatDate(emailLog.sendDateUtc)}</td>
                        <td>{emailLog.primaryRecipient}</td>
                        <td>{emailLog.subject}</td>
                        <td className="text-center">
                          <span
                            className={`${styles.statusBadge} ${getStatusBadgeClass(emailLog.status)}`}>
                            {emailLog.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {manager.hasSearchParameters && (
                <div className="mt-2 text-muted small text-center">
                  {manager.emailLogResults.length} email
                  {manager.emailLogResults.length !== 1 ? 's' : ''} found
                </div>
              )}
            </Container>
          </LoadingContainer>
        </div>
      </div>
    </Container>
  );
};
